import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  ButtonGroup,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  RadioGroup,
  Radio,
  Paper,
  Typography,
  Divider,
  Fade,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import {
  MODULE_DEFINITIONS,
  PERMISSION_LABELS,
  EXCLUSIVE_PERMISSION_GROUPS,
  ACTION_PERMISSION_GROUPS,
  EXPEDIENTES_GROUPS,
  CLIENTES_GROUPS,
  CONTACTS_GROUPS,
} from "./Editar"; // Reutiliza las definiciones de Editar

const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable } = useLocationHelpers();

  const [rolData, setRolData] = useState({ rol: "", user_level: "" });
  const [radioValue, setRadioValue] = useState("");
  const [checkedState, setCheckedState] = useState({});
  const [searchPermission, setSearchPermission] = useState("");
  const [flag, setFlag] = useState(true);

  // Inicializar checkedState
  useEffect(() => {
    const initial = {};
    Object.values(MODULE_DEFINITIONS)
      .flat()
      .forEach((mod) => {
        initial[mod.id] = {
          parentChecked: false,
          childrenChecked: Array(mod.permissions.length).fill(false),
        };
      });
    setCheckedState(initial);
  }, []);

  // Aplicar reglas de radio (Todos/Empresa/Propios)
  useEffect(() => {
    setCheckedState((prev) => {
      const newState = {};
      Object.values(MODULE_DEFINITIONS)
        .flat()
        .forEach((mod) => {
          const childrenChecked = mod.permissions.map((p, i) => {
            if (radioValue === "1") {
              if (p.includes("own") || p === "onlyassign") return false;
              return true;
            } else if (radioValue === "2") {
              if (["onlyassign", "view", "notes_own", "alerts_own"].includes(p))
                return true;
              return false;
            } else if (radioValue === "4") {
              if (["view", "notes_own", "alerts_own"].includes(p)) return true;
              return false;
            }
            return prev[mod.id]?.childrenChecked[i] ?? false;
          });
          newState[mod.id] = {
            parentChecked: childrenChecked.every(Boolean),
            childrenChecked,
          };
        });
      return newState;
    });
  }, [radioValue]);

  /* ================= HANDLERS ================= */

  const handleChangeParent = (moduleId) => {
    setCheckedState((prev) => {
      if (!prev[moduleId]) return prev;

      const mod = Object.values(MODULE_DEFINITIONS)
        .flat()
        .find((m) => m.id === moduleId);
      if (!mod) return prev;

      const value = !prev[moduleId].parentChecked;

      // Aplicamos reglas según radioValue (Todos, Empresa, Propios)
      const childrenChecked = mod.permissions.map((perm) => {
        // permisos especiales que siempre se desmarcan
        if (
          [
            "onlyassign",
            "notes_own",
            "notes_all",
            "alerts_own",
            "alerts_all",
          ].includes(perm)
        ) {
          return false;
        }

        // Comportamiento original de los radios
        if (radioValue === "1") return true; // Todos
        if (radioValue === "2") {
          if (["onlyassign", "view", "notes_own", "alerts_own"].includes(perm))
            return true;
          return false;
        }
        if (radioValue === "4") {
          if (["view", "notes_own", "alerts_own"].includes(perm)) return true;
          return false;
        }

        return value; // fallback
      });

      return {
        ...prev,
        [moduleId]: {
          parentChecked: childrenChecked.every(Boolean),
          childrenChecked,
        },
      };
    });
  };

  const handleChangeChild = (moduleId, index) => {
    setCheckedState((prev) => {
      const mod = Object.values(MODULE_DEFINITIONS)
        .flat()
        .find((m) => m.id === moduleId);
      if (!mod) return prev;

      const children = [...prev[moduleId].childrenChecked];
      const perm = mod.permissions[index];
      const newValue = !children[index];

      // Validación exclusivas
      let blocked = false;
      Object.entries(EXCLUSIVE_PERMISSION_GROUPS).forEach(
        ([groupKey, exclusivePerms]) => {
          if (exclusivePerms.includes(perm) && newValue) {
            const actionPerms = ACTION_PERMISSION_GROUPS[groupKey] || [];
            const hasAnyActionSelected = mod.permissions.some(
              (p, i) => actionPerms.includes(p) && children[i],
            );
            if (!hasAnyActionSelected) {
              Swal.fire({
                icon: "warning",
                title: "Selecciona una acción primero",
                text: "Antes de elegir si aplica a todas o solo propias, debes seleccionar una acción.",
                confirmButtonColor: "#f8ac59",
              });
              blocked = true;
            }
          }
        },
      );
      if (blocked) return prev;

      children[index] = newValue;

      // Exclusivas: si activa una, desactiva la otra
      Object.values(EXCLUSIVE_PERMISSION_GROUPS).forEach((group) => {
        if (group.includes(perm) && newValue) {
          group.forEach((p) => {
            if (p !== perm) {
              const otherIndex = mod.permissions.indexOf(p);
              if (otherIndex !== -1) children[otherIndex] = false;
            }
          });
        }
      });

      if (
        Object.values(EXCLUSIVE_PERMISSION_GROUPS).some((group) =>
          group.includes(perm),
        ) &&
        !newValue
      ) {
        const actionPerms =
          ACTION_PERMISSION_GROUPS[
            Object.keys(EXCLUSIVE_PERMISSION_GROUPS).find((key) =>
              EXCLUSIVE_PERMISSION_GROUPS[key].includes(perm),
            )
          ] || [];
        mod.permissions.forEach((p, i) => {
          if (actionPerms.includes(p)) children[i] = false;
        });
      }

      return {
        ...prev,
        [moduleId]: {
          childrenChecked: children,
          parentChecked: children.every(Boolean),
        },
      };
    });
  };

  /* ================= VALIDACIÓN ================= */
  const validateOwnAllBeforeSave = () => {
    for (const mod of Object.values(MODULE_DEFINITIONS).flat()) {
      const state = checkedState[mod.id];
      if (!state) continue;
      const children = state.childrenChecked;
      for (const [groupKey, actionPerms] of Object.entries(
        ACTION_PERMISSION_GROUPS,
      )) {
        const exclusivePerms = EXCLUSIVE_PERMISSION_GROUPS[groupKey];
        const actionSelected = mod.permissions.some(
          (p, i) => actionPerms.includes(p) && children[i],
        );
        if (!actionSelected) continue;
        const hasOwnOrAll = mod.permissions.some(
          (p, i) => exclusivePerms.includes(p) && children[i],
        );
        if (!hasOwnOrAll) return { module: mod.id, group: groupKey };
      }
    }
    return null;
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!rolData.rol || !rolData.user_level) {
      Swal.fire("Error", "Complete los campos obligatorios", "error");
      return;
    }

    const validationError = validateOwnAllBeforeSave();
    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Permisos incompletos",
        html: `En el módulo <b>${validationError.module}</b><br/>Has seleccionado <b>Eliminar</b> pero no indicaste si aplica a <b>Propias</b> o <b>Todas</b>.`,
        confirmButtonColor: "#f8ac59",
      });
      return;
    }

    const config = {};
    Object.values(MODULE_DEFINITIONS)
      .flat()
      .forEach((mod) => {
        config[mod.backendKey] = {};
        mod.permissions.forEach((perm, i) => {
          config[mod.backendKey][perm] = checkedState[mod.id]?.childrenChecked[
            i
          ]
            ? 1
            : 0;
        });
      });

    const confirm = await Swal.fire({
      title: "¿Guardar rol?",
      text: "Se creará un nuevo rol con esta configuración",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/roles/store`, {
        rol: rolData.rol,
        user_level: rolData.user_level,
        config,
      });

      Swal.fire({
        title: "Guardado",
        text: "Rol creado correctamente",
        icon: "success",
        confirmButtonColor: "#1ab394",
      }).then(() => navigate("/roles"));
    } catch {
      Swal.fire("Error", "No se pudo guardar", "error");
    }
  };

  /* ================= RENDER PERMISOS ================= */
  const renderPermissions = (mod) => {
    const groups =
      mod.id === "Expedientes"
        ? EXPEDIENTES_GROUPS
        : mod.id === "Clientes"
          ? CLIENTES_GROUPS
          : mod.id === "Contactos"
            ? CONTACTS_GROUPS
            : [{ title: "", permissions: mod.permissions }];

    return groups.map((group) => (
      <Grid item xs={12} key={group.title}>
        {group.title && (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              display: "block",
              mb: 0.5,
              mt: 1,
              pl: 1,
              color: "text.secondary",
              borderLeft: "3px solid #1976d2",
            }}>
            {group.title}
          </Typography>
        )}
        <Grid container spacing={0.5} sx={{ pl: 1 }}>
          {group.permissions
            .filter((perm) =>
              perm.toLowerCase().includes(searchPermission.toLowerCase()),
            )
            .map((perm) => {
              const permIndex = mod.permissions.indexOf(perm);
              if (permIndex === -1) return null;
              return (
                <Grid item xs={6} sm={4} md={2} key={perm}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": { fontSize: 12 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          checkedState[mod.id]?.childrenChecked[permIndex] ||
                          false
                        }
                        onChange={() => handleChangeChild(mod.id, permIndex)}
                      />
                    }
                    label={PERMISSION_LABELS[perm]}
                  />
                </Grid>
              );
            })}
        </Grid>
      </Grid>
    ));
  };

  const renderSection = (title, modules) => (
    <>
      <Grid item xs={12}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mb: 1, mt: 2, color: "text.secondary" }}>
          {title}
        </Typography>
      </Grid>
      {modules.map((mod) => (
        <Grid item xs={12} key={mod.id}>
          <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#fafafa" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "#f0f2f5",
              }}>
              <Checkbox
                size="small"
                checked={checkedState[mod.id]?.parentChecked || false}
                onChange={() => handleChangeParent(mod.id)}
              />
              <Typography fontWeight="bold" fontSize={14}>
                {mod.id}
              </Typography>
              <TextField
                size="small"
                placeholder="Buscar permiso"
                value={searchPermission}
                onChange={(e) => setSearchPermission(e.target.value)}
                sx={{ ml: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={0.5}>
              {renderPermissions(mod)}
            </Grid>
          </Paper>
        </Grid>
      ))}
    </>
  );

  /* ================= RENDER ================= */
  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Roles" subTitle="Crear" />
      <Fade in={flag} timeout={800}>
        <Paper sx={{ p: 4 }}>
          <Grid container spacing={2}>
            {/* DATOS */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography fontWeight="bold">Datos del rol</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre del rol"
                      value={rolData.rol}
                      onChange={(e) =>
                        setRolData({ ...rolData, rol: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Nivel"
                      value={rolData.user_level}
                      onChange={(e) =>
                        setRolData({ ...rolData, user_level: e.target.value })
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ACCESO */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight="bold">Nivel de acceso</Typography>
                <Divider sx={{ mb: 2 }} />
                <FormControl>
                  <RadioGroup
                    row
                    value={radioValue}
                    onChange={(e) => setRadioValue(e.target.value)}>
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Todos"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio />}
                      label="Empresa"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio />}
                      label="Propios"
                    />
                  </RadioGroup>
                </FormControl>
              </Paper>
            </Grid>

            {/* PERMISOS */}
            {renderSection("Módulos principales", MODULE_DEFINITIONS.core)}
            {renderSection("Configuración", MODULE_DEFINITIONS.configuracion)}
            {renderSection("Otros", MODULE_DEFINITIONS.otros)}

            {/* ACCIONES */}
            <Grid item xs={12}>
              <ButtonGroup fullWidth size="large">
                <Button color="success" onClick={handleSave}>
                  Guardar
                </Button>
                <Button color="error" onClick={() => goTable("roles", "Roles")}>
                  Tabla
                </Button>
                <Button onClick={goPanel}>Inicio</Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </div>
  );
};

export default Crear;
